export const HIGH_SCORE_KEY = "super-quint-bros-high-score";
export const STARTING_LIVES = 3;
export const STARTING_TIME = 400;
export const COINS_PER_LIFE = 100;

export type Power = "small" | "super" | "fire";

/** Intermediate enemy state carried across sub-areas (e.g. a troopa left as a shell). */
export interface PersistedEnemyState {
  role: "shell";
  x: number;
}

export interface PersistedLevelState {
  timeLeft: number;
  usedBlocks: string[];
  brokenBlocks: string[];
  collectedCoins: string[];
  defeatedEnemies: string[];
  enemyStates: Record<string, PersistedEnemyState>;
}

/** Points for consecutive enemies hit by one player-kicked shell, as in the original. */
export const SHELL_CHAIN_SCORES = [500, 800, 1000, 2000, 4000, 5000, 8000] as const;
export const SHELL_KICK_SCORE = 400;

/**
 * Score for the n-th enemy (1-based) hit by a moving shell. Past the table the
 * chain awards a life instead of points.
 */
export function shellChainScore(hitIndex: number): { score: number; life: boolean } {
  if (hitIndex <= 0) return { score: 0, life: false };
  if (hitIndex > SHELL_CHAIN_SCORES.length) return { score: 0, life: true };
  return { score: SHELL_CHAIN_SCORES[hitIndex - 1] ?? 0, life: false };
}

export interface RunState {
  lives: number;
  score: number;
  coins: number;
  power: Power;
  levelId: string;
  levelStates: Record<string, PersistedLevelState>;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function createRun(levelId = "1-1"): RunState {
  return {
    lives: STARTING_LIVES,
    score: 0,
    coins: 0,
    power: "small",
    levelId,
    levelStates: {},
  };
}

export function levelStateFor(
  run: RunState,
  levelId: string,
  initialTime: number,
): PersistedLevelState {
  const existing = run.levelStates[levelId];
  if (existing) {
    existing.enemyStates ??= {};
    return existing;
  }
  const state: PersistedLevelState = {
    timeLeft: initialTime,
    usedBlocks: [],
    brokenBlocks: [],
    collectedCoins: [],
    defeatedEnemies: [],
    enemyStates: {},
  };
  run.levelStates[levelId] = state;
  return state;
}

export function addScore(run: RunState, amount: number): void {
  run.score += amount;
}

export function addCoins(run: RunState, amount = 1): { livesGained: number } {
  const before = Math.floor(run.coins / COINS_PER_LIFE);
  run.coins += amount;
  const after = Math.floor(run.coins / COINS_PER_LIFE);
  const livesGained = after - before;
  run.lives += livesGained;
  return { livesGained };
}

export function flagScore(heightTiles: number): number {
  if (heightTiles >= 8) return 5000;
  if (heightTiles >= 6) return 2000;
  if (heightTiles >= 4) return 800;
  if (heightTiles >= 2) return 400;
  return 100;
}

export function loadHighScore(storage: StorageLike): number {
  const raw = storage.getItem(HIGH_SCORE_KEY);
  const value = raw ? Number.parseInt(raw, 10) : 0;
  return Number.isFinite(value) ? value : 0;
}

export function saveHighScore(storage: StorageLike, score: number): void {
  const best = Math.max(score, loadHighScore(storage));
  storage.setItem(HIGH_SCORE_KEY, String(best));
}

export function padScore(score: number, width = 6): string {
  return Math.max(0, Math.floor(score)).toString().padStart(width, "0");
}

export function padTime(time: number): string {
  return Math.max(0, Math.floor(time)).toString().padStart(3, "0");
}
